import React, { useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, LockKeyhole } from "lucide-react";

const HERO = "https://images.pexels.com/photos/7951657/pexels-photo-7951657.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940";

export function ResetPasswordView({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const goHome = () => { window.location.href = "/"; };

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      toast.success("Your password has been reset");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reset your password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen w-full md:grid-cols-2" data-testid="reset-password-view">
      <div className="relative hidden md:block">
        <img src={HERO} alt="Family through the years" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#082D4A]/60" />
        <div className="absolute left-10 top-8 flex items-center gap-2.5 text-white">
          <img src="/logo-mark.png" alt="" className="h-10 w-10 rounded-lg bg-white/95 p-1" />
          <span className="font-brand text-lg">Where Am i From<span className="text-[#D4A017]">.net</span></span>
        </div>
        <div className="absolute bottom-14 left-10 right-10 text-white">
          <h1 className="font-serif text-5xl font-bold leading-[1.05] lg:text-6xl">Welcome<br />back home</h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-white/85">
            Choose a new password and jump right back into your family space.
          </p>
        </div>
      </div>

      <div className="flex h-full items-center justify-center overflow-y-auto bg-[#F4F6F8] p-8">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 md:hidden">
            <img src="/logo-mark.png" alt="" className="h-10 w-10 object-contain" />
            <span className="font-brand text-lg text-[#082D4A]">Where Am i From<span className="text-[#D4A017]">.net</span></span>
          </div>

          {!token ? (
            <div className="text-center" data-testid="reset-invalid">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#E8EEF3] text-[#082D4A]"><LockKeyhole size={26} /></div>
              <h2 className="font-serif text-2xl font-bold text-[#082D4A]">Link not valid</h2>
              <p className="mt-2 text-sm text-[#64748B]">This reset link is missing or malformed. Please request a new one from the sign-in screen.</p>
              <Button onClick={goHome} data-testid="reset-go-home" className="mt-6 rounded-full bg-[#082D4A] text-white hover:bg-[#08223A]">Back to sign in</Button>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#007F6B]/10 text-[#007F6B]"><CheckCircle2 size={30} /></div>
              <h2 className="font-serif text-2xl font-bold text-[#082D4A]">Password updated</h2>
              <p className="mt-2 text-sm text-[#64748B]">You can now sign in with your new password.</p>
              <Button onClick={goHome} data-testid="reset-signin-now" className="mt-6 rounded-full bg-[#D4A017] text-white hover:bg-[#B0850F]">Go to sign in</Button>
            </div>
          ) : (
            <>
              <h2 className="font-serif text-3xl font-bold text-[#082D4A]">Choose a new password</h2>
              <p className="mt-2 text-sm text-[#64748B]">Enter a new password for your family space. This link works only once.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div className="space-y-1.5">
                  <Label>New password</Label>
                  <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" placeholder="At least 6 characters" autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label>Confirm new password</Label>
                  <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 bg-white" placeholder="Re-enter password" />
                </div>
                <Button type="submit" disabled={busy} data-testid="reset-submit-button" className="h-11 w-full rounded-full bg-[#D4A017] text-base text-white hover:bg-[#B0850F]">
                  <KeyRound size={17} className="mr-2" />
                  {busy ? "Saving…" : "Reset password"}
                </Button>
              </form>
              <button type="button" onClick={goHome} data-testid="reset-back-signin" className="mt-6 w-full text-sm font-medium text-[#082D4A] hover:underline">Back to sign in</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
